import React, { useState, useCallback, ChangeEvent, useEffect } from 'react';
import Modal from '../../../components/Modal/NewModal';
import { TemplateSelect } from '../../../components/TemplateSelect';
import { useTemplateName } from '../../../hooks/useTemplateList';
import { useDebounce } from 'use-debounce';
import { TextField } from '@td-client-frontend/common';
import { toast } from 'react-toastify';
import { Button } from '../../../components/Button';

interface CopyTemplateModalProps {
  isOpen: boolean;
  toggleCopyTemplateModal: () => void;
  copyTemplate: (newName: string, oldName: string) => Promise<number>;
  onCopySuccess: (newName: string) => void;
}

const efficientValueLength = 3;

export function CopyTemplateModal({
  isOpen,
  toggleCopyTemplateModal,
  copyTemplate,
  onCopySuccess
}: CopyTemplateModalProps) {
  const [templateName, setTemplateName] = useState<string>('');
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const [newTemplateNameError, setNewTemplateNameError] = useState<string | null>(null);

  const [debouncedTemplateNameSearch] = useDebounce<string>(newTemplateName, 750);

  const {
    data: isAvailable,
    isFetching,
    refetch
  } = useTemplateName({
    search: debouncedTemplateNameSearch
  });

  const hasValidName = isAvailable && !isFetching;

  const handleCopyTemplate = useCallback(() => {
    if (templateName && newTemplateName?.length >= efficientValueLength) {
      copyTemplate(newTemplateName, templateName)
        .then(() => {
          onCopySuccess(newTemplateName);
          toggleCopyTemplateModal();
          toast.success(`${newTemplateName}.json was successfully copied!`, {
            position: 'top-center',
            hideProgressBar: true,
            pauseOnHover: true,
            draggable: true
          });
        })
        .catch(() => {
          setNewTemplateNameError(
            `New Template Name is already in use or is less than ${efficientValueLength} characters long.`
          );
        });
    } else {
      setNewTemplateNameError(
        `New Template Name is already in use or is less than ${efficientValueLength} characters long.`
      );
    }
  }, [copyTemplate, newTemplateName, onCopySuccess, templateName, toggleCopyTemplateModal]);

  const handleNewTemplateName = (event: ChangeEvent<HTMLInputElement>) => {
    const newTemplateName = event.target.value;
    const re = /[^A-Za-z\d;]+/g;
    if (newTemplateName?.length < efficientValueLength) {
      return setNewTemplateNameError(`New Template Name is less than ${efficientValueLength} characters long`);
    }
    if (newTemplateName.match(re) || newTemplateName.length >= 20) {
      return setNewTemplateNameError('New Template Name must be valid');
    }
    setNewTemplateName(newTemplateName);
    setNewTemplateNameError(null);
    refetch();
  };

  useEffect(() => {
    if (!isFetching && newTemplateName.length) {
      return setNewTemplateNameError(isAvailable ? null : 'New Template Name is already in use');
    }
  }, [isAvailable, isFetching, newTemplateName.length, templateName.length]);

  const onCloseHandler = useCallback(() => {
    toggleCopyTemplateModal();
    setTemplateName('');
    setNewTemplateName('');
    setNewTemplateNameError(null);
  }, [toggleCopyTemplateModal]);

  const CustomModalControls = useCallback(
    () => (
      <div className="flex gap-4">
        <Button kind="secondary" onClick={onCloseHandler} title="Cancel" />
        <Button
          onClick={handleCopyTemplate}
          tooltip="Please enter all required fields"
          id="copy-template-button"
          disabled={!(hasValidName && templateName.length && newTemplateName.length && !newTemplateNameError?.length)}
          title="Copy"
        />
      </div>
    ),
    [
      handleCopyTemplate,
      newTemplateName.length,
      newTemplateNameError,
      onCloseHandler,
      templateName.length,
      hasValidName
    ]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseHandler}
      header="Copy From Existing Template"
      size="default"
      customFooterActions={{
        right: <CustomModalControls />
      }}
    >
      <div className="flex flex-col w-full">
        <TemplateSelect label="Select Template *" onSelected={setTemplateName} />
        <div className="flex flex-col min-w-24 px-6">
          <TextField
            label="New Template Name *"
            name="newTemplateName"
            type="text"
            isError={newTemplateNameError?.length}
            error={newTemplateNameError}
            placeholder="Please enter new template name"
            onChange={handleNewTemplateName}
          />
        </div>
      </div>
    </Modal>
  );
}
